// GalleryWall — Desenio-Postermotive mit täglich wechselnden Pflege-Sprüchen
import React, { useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { toast } from 'sonner';
import { Download, Loader2, Share2 } from 'lucide-react';

const STATEMENTS = [
  { top: 'Zähl nicht\ndie Tage.', bottom: 'Mach, dass\ndie Tage\nzählen.' },
  { top: 'Pflege ist\nkein Job.', bottom: 'Pflege ist\nHaltung.' },
  { top: 'Kleine\nSchritte —', bottom: 'jeden Tag\neinen.' },
  { top: 'Du lernst nicht\nfür die Prüfung.', bottom: 'Du lernst\nfür Menschen.' },
  { top: 'Erst der\nKaffee,', bottom: 'dann das\nKapitel.' },
  { top: 'Wissen\nberuhigt.', bottom: 'Können\nträgt.' },
  { top: 'Heute 1%\nbesser', bottom: 'als\ngestern.' },
  { top: 'Hände, die\npflegen,', bottom: 'verändern\ndie Welt.' },
  { top: 'Dranbleiben', bottom: 'schlägt\nTalent.' },
  { top: 'Ruhe\nbewahren.', bottom: 'Weiter-\nmachen.' },
  { top: 'Dein Wissen', bottom: 'ist ihre\nSicherheit.' },
  { top: 'Mut\nsteht dir.', bottom: 'Weiter\ngeht’s.' },
];

const BOTANICALS = [
  'Pflege wächst\nin kleinen Schritten.',
  'Ein Kapitel am Tag\nlässt Wissen wachsen.',
  'Auch Pausen\ngehören zum Lernen.',
  'Wurzeln zuerst,\ndann Flügel.',
  'Geduld ist auch\neine Pflegekompetenz.',
  'Jede Schicht\nschreibt Geschichte.',
];

const HARMONIES = [
  'Du bist die Ruhe\nim Sturm.',
  'Dein Lächeln\nist Medizin.',
  'Herz schlägt\nHektik.',
  'Empathie ist\ndeine Superkraft.',
  'Atme. Du machst\ndas großartig.',
  'Du wirst gebraucht —\ngenau so, wie du bist.',
];

const dayIndex = () => Math.floor(Date.now() / 86400000);

const seedFrom = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

const nl = (text) => text.split('\n').map((l, i) => (
  <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>
));

// Poster-DOM als PNG rendern (inkl. Rahmen), speichern oder per Web-Share teilen
const renderPoster = async (node) => toBlob(node, { pixelRatio: 3, cacheBust: true });

const PosterActions = ({ targetRef, name, brand = 'Dickies Helden' }) => {
  const [busy, setBusy] = useState(null);
  const slug = brand.toLowerCase().replace(/[^a-z]+/g, '-');
  const fileName = `${slug}-poster-${name}.png`;

  const save = async () => {
    setBusy('save');
    try {
      const blob = await renderPoster(targetRef.current);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
      toast.success('Poster gespeichert — teile es mit deinem Team!');
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const share = async () => {
    setBusy('share');
    try {
      const blob = await renderPoster(targetRef.current);
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${brand} · Poster des Tages`,
          text: `Heutiges Poster aus der ${brand}-Galerie 🧡`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName; a.click();
        URL.revokeObjectURL(url);
        toast.success('Teilen wird hier nicht unterstützt — Poster wurde stattdessen gespeichert.');
      }
    } catch (e) { if (e?.name !== 'AbortError') toast.error('Teilen fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const btn = 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] border border-cream-300 bg-cream-50 text-sand-600 hover:text-espresso hover:border-terra/40 transition-colors';
  return (
    <div className="flex justify-center gap-2 mt-3">
      <button onClick={save} disabled={!!busy} className={btn} data-testid={`poster-save-${name}`}>
        {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Speichern
      </button>
      <button onClick={share} disabled={!!busy} className={btn} data-testid={`poster-share-${name}`}>
        {busy === 'share' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} Teilen
      </button>
    </div>
  );
};

const Frame = ({ rotate, delay, children, testId, name, shareable = true }) => {
  const ref = useRef(null);
  return (
    <div>
      <div style={{ transform: `rotate(${rotate}deg)` }}>
        <div
          ref={ref}
          data-testid={testId}
          className="dh-frame dh-lift animate-fade-in-up"
          style={{ animationDelay: `${delay}ms` }}
        >
          <div className="dh-mat">{children}</div>
        </div>
      </div>
      {shareable && <PosterActions targetRef={ref} name={name} />}
    </div>
  );
};

const BotanicalSvg = () => (
  <svg viewBox="0 0 150 190" className="w-24 h-32 sm:w-28 sm:h-36" fill="none" stroke="#522A25" strokeWidth="2.5" strokeLinecap="round" opacity="0.75">
    <path d="M75 185 C 70 130, 78 80, 68 10" />
    <path d="M70 40 C 50 32, 38 36, 28 50 C 44 56, 60 52, 70 40 Z" />
    <path d="M71 72 C 92 62, 106 66, 116 82 C 98 88, 80 84, 71 72 Z" />
    <path d="M72 104 C 52 96, 40 100, 30 114 C 46 120, 62 116, 72 104 Z" />
    <path d="M73 136 C 94 126, 108 130, 118 146 C 100 152, 82 148, 73 136 Z" />
  </svg>
);

const SunburstSvg = () => (
  <svg viewBox="0 0 100 100" className="w-20 h-20 sm:w-24 sm:h-24">
    {Array.from({ length: 18 }).map((_, i) => {
      const a = (i * 20 * Math.PI) / 180;
      return (
        <line
          key={i}
          x1={50 + Math.cos(a) * 16} y1={50 + Math.sin(a) * 16}
          x2={50 + Math.cos(a) * 42} y2={50 + Math.sin(a) * 42}
          stroke="#FBF6F0" strokeWidth="7" strokeLinecap="round"
        />
      );
    })}
    <circle cx="50" cy="50" r="10" fill="#FBF6F0" />
  </svg>
);

// Kompaktes Poster für Kurs- & Modulseiten — Spruch wechselt täglich, pro Seite verschieden
export const MiniPoster = ({ seed = '', className = '', brand = 'Dickies Helden' }) => {
  const q = STATEMENTS[(dayIndex() + seedFrom(seed)) % STATEMENTS.length];
  const ref = useRef(null);
  return (
    <div className={`flex flex-col items-center ${className}`} data-testid="mini-poster">
      <div style={{ transform: 'rotate(-0.8deg)' }}>
        <div ref={ref} className="dh-frame dh-lift inline-block">
          <div className="dh-mat">
            <div className="px-8 py-7 sm:px-12 sm:py-8 text-center" style={{ backgroundColor: '#E07A5F' }}>
              <p className="uppercase leading-[0.95]" style={{ fontFamily: "'League Gothic', sans-serif", fontSize: 'clamp(24px, 3vw, 34px)', color: '#FBF6F0' }}>
                {nl(q.top)}
              </p>
              <p className="uppercase leading-[0.95] mt-2 inline-block" style={{ fontFamily: "'League Gothic', sans-serif", fontSize: 'clamp(30px, 3.6vw, 42px)', color: '#FBF6F0', transform: 'rotate(-4deg)' }}>
                {nl(q.bottom)}
              </p>
              <p className="mt-4 text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: 'rgba(251,246,240,0.7)' }}>
                {brand} · Galerie
              </p>
            </div>
          </div>
        </div>
      </div>
      <PosterActions targetRef={ref} name="des-tages" brand={brand} />
    </div>
  );
};

export const GalleryWall = () => {
  const d = dayIndex();
  const statement = STATEMENTS[d % STATEMENTS.length];
  const botanical = BOTANICALS[d % BOTANICALS.length];
  const harmony = HARMONIES[d % HARMONIES.length];

  return (
    <section className="mb-12" data-testid="gallery-wall">
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="w-6 h-[2px] bg-terra" />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-terra">Galerie-Wand · Heute für dich</span>
      </div>
      <h2 className="font-display text-3xl text-espresso tracking-tight leading-tight mb-6">
        Worte, die <span className="italic text-terra">tragen</span>.
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 items-start">
        <Frame rotate={-1.2} delay={0} testId="poster-statement" name="statement">
          <div className="aspect-[3/4] flex flex-col justify-center px-5 py-6 overflow-hidden" style={{ backgroundColor: '#E07A5F' }}>
            <p className="uppercase leading-[0.95]" style={{ fontFamily: "'League Gothic', sans-serif", fontSize: 'clamp(26px, 2.2vw, 34px)', color: '#FBF6F0' }}>
              {nl(statement.top)}
            </p>
            <p className="uppercase leading-[0.95] mt-3" style={{ fontFamily: "'League Gothic', sans-serif", fontSize: 'clamp(32px, 2.8vw, 44px)', color: '#FBF6F0', transform: 'rotate(-5deg)', transformOrigin: 'left center' }}>
              {nl(statement.bottom)}
            </p>
            <p className="mt-5 text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: 'rgba(251,246,240,0.7)' }}>
              Dickies Helden · Nº {String((d % STATEMENTS.length) + 1).padStart(2, '0')}
            </p>
          </div>
        </Frame>

        <Frame rotate={0.8} delay={120} testId="poster-botanical" name="botanica">
          <div className="aspect-[3/4] flex flex-col items-center justify-center gap-5 px-5 py-6" style={{ backgroundColor: '#FBF6F0' }}>
            <BotanicalSvg />
            <div className="text-center">
              <p className="font-display italic text-[19px] leading-snug text-espresso">
                {nl(botanical)}
              </p>
              <p className="mt-3 text-[9px] uppercase tracking-[0.3em] font-bold text-espresso/40">
                Botanica · Studio DH
              </p>
            </div>
          </div>
        </Frame>

        <Frame rotate={-0.6} delay={240} testId="poster-harmony" name="harmony">
          <div className="aspect-[3/4] flex flex-col items-center justify-center gap-5 px-5 py-6" style={{ backgroundColor: '#E9A0AC' }}>
            <SunburstSvg />
            <div className="text-center">
              <p className="font-display italic text-[20px] leading-snug" style={{ color: '#522A25' }}>
                {nl(harmony)}
              </p>
              <p className="mt-3 text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: 'rgba(82,42,37,0.5)' }}>
                Harmony · MMXXVI
              </p>
            </div>
          </div>
        </Frame>
      </div>
    </section>
  );
};

export default GalleryWall;
